import React, { useEffect, useState } from 'react';
import { CentrifugalHeader, CentrifugalPageContainer } from './styled';
import EcoSelectStation from '@/comp/EcoSelectStation/EcoSelectStation';
import { permissions } from '@/constants';
import { observer } from 'mobx-react';
import { ENGINE_VERSION_BASE, ENGINE_VERSION_BASE_MIN, EngineConfigStatus } from '@/store/Engine/constants';
import isEmpty from 'lodash/isEmpty';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';

import { t } from 'i18next';
import VersionHistory from '../../VersionHistory/VersionHistory';
import Wizard from '../Wizard/Wizard';
import EngineVersion from '../../EngineVersion';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { formatEngineConfigDate } from '@/utils/util-functions';
import { EPBetaTag } from '@/comp/common/EPTag';
import CentrifugalVersion from './CentrifugalVersion';
import SpinCentered from '@/comp/SpinCentered';
import { useIsMobile } from '@/hooks/useIsMobile';
import useScrollDrawer from '@/hooks/useDrawer';
import { DrawerContext } from '@/context/DrawerContext';
import {
  fetchEngineSupport,
  getTabTitle,
  handleActiveTabChange,
  handleTabChange,
  versionTabs,
} from './CentrifugalCompressors.utils';
import StationStages from '@/comp/StationStages/StationStages';
import { Tabs } from 'antd/lib';
import PIDChangesModal from '../../PIDChangesModal/PIDChangesModal';

const CentrifugalPage = observer(() => {
  const { permissionStore, engineStore, stationStore } = useStores();
  const navigate = useNavigate();
  const { tvalue } = useMeasurement();
  const { isMobile } = useIsMobile();

  const [showDrawer, setShowDrawer] = useScrollDrawer('scroll-container-centrifugal', isMobile) as [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ];

  const [activeTab, setActiveTab] = useState(null);
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);
  const [confirmationModalInfo, setConfirmationModalInfo] = useState({
    open: false,
    modalText: undefined,
    actionHandler: undefined,
  });

  const [, setAdminModalOpen] = useState(false);

  const hasViewPermission = permissionStore.isAuth(permissions.VIEW_STATION_ENGINE);
  const hasUpdatePermission = permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE);

  useEffect(() => {
    (async () => {
      if (!hasViewPermission) {
        navigate('/home');
      } else {
        engineStore.clearEngineConfig();
        await engineStore.getEngineConfig(tvalue);

        if (!hasViewPermission && isEmpty(engineStore.publishedEngineConfig)) {
          setAdminModalOpen(true);
        } else {
          if (engineStore.hasFinishedWizard) {
            if (!isEmpty(engineStore.publishedEngineConfig)) {
              setActiveTab(EngineConfigStatus.Published);
              engineStore.setEngineConfig(engineStore.publishedEngineConfig, tvalue);
            } else {
              setActiveTab(engineStore.configurationStatus);
            }
          } else {
            setActiveTab(versionTabs.engine);
          }
        }
      }
    })();
  }, [stationStore.currentStation?.id, hasViewPermission, hasUpdatePermission]);

  useEffect(() => {
    fetchEngineSupport(engineStore, stationStore, navigate);
  }, [stationStore.currentStation?.id]);

  useEffect(() => {
    if (activeTab) {
      handleActiveTabChange(activeTab, engineStore, tvalue);
    }
  }, [activeTab]);

  const onTabChange = async tab => {
    await handleTabChange(tab, engineStore, tvalue, setActiveTab, hasViewPermission);
  };

  const TabTitle = observer(({ engineConfigObj }) => {
    const tabTitle = getTabTitle(engineConfigObj, engineStore);

    return (
      <span className={!hasViewPermission && activeTab !== 'published' ? 'disabled-tab' : undefined}>
        <span className={cn('bold-text', !hasViewPermission ? 'disabled' : undefined)}>{t(tabTitle)}</span>
        {tabTitle !== 'createNewDraft' && tabTitle !== 'recommended' && (
          <span className={!hasViewPermission ? 'disabled' : undefined}>
            &nbsp;({ENGINE_VERSION_BASE}
            {`${engineConfigObj.version ?? ENGINE_VERSION_BASE_MIN}`})
          </span>
        )}
      </span>
    );
  });

  const mainTabItems = [
    ...(engineStore.hasFinishedWizard
      ? [
          ...(!isEmpty(engineStore.publishedEngineConfig)
            ? [
                {
                  label: <TabTitle engineConfigObj={engineStore.publishedEngineConfig} />,
                  key: versionTabs.published,
                  children: <CentrifugalVersion activeTab={activeTab} setActiveTab={setActiveTab} />,
                },
                {
                  label: <TabTitle engineConfigObj={engineStore.draftEngineConfig} />,
                  key: versionTabs.draft,
                  children: <CentrifugalVersion activeTab={activeTab} setActiveTab={setActiveTab} />,
                },
                {
                  label: (
                    <span className={cn('bold-text', !hasViewPermission && 'disabled-tab')}>{t('versionHistory')}</span>
                  ),
                  key: versionTabs.versionHistory,
                  children: <VersionHistory />,
                },
              ]
            : [
                {
                  label: <TabTitle engineConfigObj={engineStore.draftEngineConfig} />,
                  key: versionTabs.draft,
                  children: <CentrifugalVersion activeTab={activeTab} setActiveTab={setActiveTab} />,
                },
              ]),
        ]
      : [
          {
            label: t('wizard'),
            key: versionTabs.engine,
            children: (
              <Wizard
                setActiveTab={setActiveTab}
                hasTriedToSubmit={hasTriedToSubmit}
                setHasTriedToSubmit={setHasTriedToSubmit}
                confirmationModalInfo={!engineStore.hasFinishedWizard ? confirmationModalInfo : null}
                setConfirmationModalInfo={setConfirmationModalInfo}
              />
            ),
          },
        ]),
    ...(!isEmpty(engineStore.automaticEngineConfig)
      ? [
          {
            label: (
              <ConditionalToolTip
                condition={!window.localStorage.getItem('hasVisitedRecommendedTab')}
                content={t('newRecommendedConfiguration', {
                  createdDate: formatEngineConfigDate(engineStore.automaticEngineConfig?.createdDate),
                })}
                placement="top"
              >
                <div className={'recommendation-tab'}>
                  <TabTitle engineConfigObj={engineStore.automaticEngineConfig} />
                  {!window.localStorage.getItem('hasVisitedRecommendedTab') && (
                    <span className="new-recommendation-indication">*</span>
                  )}
                  <EPBetaTag className="engine-beta-tag" />
                </div>
              </ConditionalToolTip>
            ),
            key: versionTabs.recommendation,
            children: (
              <EngineVersion
                setParentActiveTab={setActiveTab}
                parentActiveTab={activeTab}
                hasTriedToSubmit={hasTriedToSubmit}
                setHasTriedToSubmit={setHasTriedToSubmit}
                confirmationModalInfo={confirmationModalInfo}
                setConfirmationModalInfo={setConfirmationModalInfo}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <DrawerContext.Provider value={{ showDrawer, setShowDrawer }}>
      <CentrifugalPageContainer data-testid="scroll-container-centrifugal">
        {engineStore.isLoadingWizard ? (
          <SpinCentered />
        ) : (
          <CentrifugalHeader>
            <div className="header">
              <EcoSelectStation />
              <StationStages />
            </div>
            <Tabs
              key={activeTab}
              activeKey={activeTab}
              onChange={onTabChange}
              items={mainTabItems}
              animated={{
                inkBar: true,
                tabPane: false,
              }}
            />
          </CentrifugalHeader>
        )}
      </CentrifugalPageContainer>
      <PIDChangesModal />
    </DrawerContext.Provider>
  );
});

export default CentrifugalPage;
